import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, ArrowLeftRight, Users } from 'lucide-react'
import {
  fetchTasks,
  fetchCompetitionParticipants,
  fetchTaskComparison,
  type AlbatrossTask,
  type AlbatrossParticipant,
  type TaskComparison,
} from '../../services/albatross'
import { useActiveCompetition } from '../../hooks/useActiveCompetition'
import { Sparkline } from './Sparkline'

interface AggregatedStats {
  climbRate: number | null
  glideRatio: number | null
  speed: number | null
  decisionQuality: number | null
  tasksCompleted: number
  goalsReached: number
}

function average(values: (number | null | undefined)[]): number | null {
  const nums = values.filter((v): v is number => v != null)
  if (nums.length === 0) return null
  return nums.reduce((a, b) => a + b, 0) / nums.length
}

function aggregate(comparisons: TaskComparison[], pilotKey: 'pilot1' | 'pilot2'): AggregatedStats {
  const pilots = comparisons.map(c => c[pilotKey])
  return {
    climbRate: average(pilots.map(p => p.climbRate)),
    glideRatio: average(pilots.map(p => p.glideRatio)),
    speed: average(pilots.map(p => p.speed)),
    decisionQuality: average(pilots.map(p => p.decisionQuality)),
    tasksCompleted: pilots.filter(p => p.rank != null).length,
    goalsReached: pilots.filter(p => p.goalReached === true).length,
  }
}

function fmt(val: number | null | undefined, decimals = 1, unit = ''): string {
  if (val == null) return '—'
  return `${val.toFixed(decimals)}${unit}`
}

interface StatCardProps {
  label: string
  val1: number | null
  val2: number | null
  name1: string
  name2: string
  decimals?: number
  unit?: string
  higherIsBetter?: boolean
}

function StatCard({ label, val1, val2, name1, name2, decimals = 1, unit = '', higherIsBetter = true }: StatCardProps) {
  const winner =
    val1 != null && val2 != null
      ? higherIsBetter
        ? val1 > val2 ? 'pilot1' : val2 > val1 ? 'pilot2' : 'tie'
        : val1 < val2 ? 'pilot1' : val2 < val1 ? 'pilot2' : 'tie'
      : null

  return (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <div className={`flex-1 ${winner === 'pilot1' ? 'text-fwo-blue font-semibold' : 'text-gray-700'}`}>
          <p className="text-lg">{fmt(val1, decimals, unit)}</p>
          <p className="text-xs text-gray-500 truncate">{name1}</p>
        </div>
        <div className="text-gray-300 text-sm">vs</div>
        <div className={`flex-1 ${winner === 'pilot2' ? 'text-fwo-blue font-semibold' : 'text-gray-700'}`}>
          <p className="text-lg">{fmt(val2, decimals, unit)}</p>
          <p className="text-xs text-gray-500 truncate">{name2}</p>
        </div>
      </div>
    </div>
  )
}

export function HeadToHead() {
  const { albatrossCompId } = useActiveCompetition()
  const [tasks, setTasks] = useState<AlbatrossTask[]>([])
  const [participants, setParticipants] = useState<AlbatrossParticipant[]>([])
  const [pilot1Id, setPilot1Id] = useState<string>('')
  const [pilot2Id, setPilot2Id] = useState<string>('')
  const [comparisons, setComparisons] = useState<TaskComparison[]>([])
  const [loadingInit, setLoadingInit] = useState(true)
  const [loadingComparison, setLoadingComparison] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load tasks and participants when competition ID is available
  useEffect(() => {
    if (!albatrossCompId) {
      setLoadingInit(false)
      return
    }
    let cancelled = false
    async function init() {
      setLoadingInit(true)
      setError(null)
      try {
        const taskList = await fetchTasks(albatrossCompId)
        if (cancelled) return
        setTasks(taskList)
        const pts = await fetchCompetitionParticipants(albatrossCompId, taskList)
        if (cancelled) return
        setParticipants(pts)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load data from Albatross')
      } finally {
        if (!cancelled) setLoadingInit(false)
      }
    }
    init()
    return () => { cancelled = true }
  }, [albatrossCompId])

  const runComparison = useCallback(async () => {
    if (!pilot1Id || !pilot2Id || pilot1Id === pilot2Id) return
    setLoadingComparison(true)
    setError(null)
    setComparisons([])

    const results: TaskComparison[] = []
    for (const task of tasks) {
      try {
        const c = await fetchTaskComparison(task.id, pilot1Id, pilot2Id)
        // Attach task metadata if not already present
        results.push({
          ...c,
          taskName: c.taskName ?? task.name,
          date: c.date ?? task.date,
        })
      } catch {
        // Skip tasks where comparison data is unavailable
      }
    }
    setComparisons(results)
    setLoadingComparison(false)
  }, [pilot1Id, pilot2Id, tasks])

  const pilot1Name = participants.find(p => p.id === pilot1Id)?.name ?? '—'
  const pilot2Name = participants.find(p => p.id === pilot2Id)?.name ?? '—'

  const stats1 = comparisons.length > 0 ? aggregate(comparisons, 'pilot1') : null
  const stats2 = comparisons.length > 0 ? aggregate(comparisons, 'pilot2') : null

  const climbData1 = comparisons.map(c => c.pilot1.climbRate ?? null)
  const climbData2 = comparisons.map(c => c.pilot2.climbRate ?? null)
  const speedData1 = comparisons.map(c => c.pilot1.speed ?? null)
  const speedData2 = comparisons.map(c => c.pilot2.speed ?? null)
  const glideData1 = comparisons.map(c => c.pilot1.glideRatio ?? null)
  const glideData2 = comparisons.map(c => c.pilot2.glideRatio ?? null)
  const dqData1 = comparisons.map(c => c.pilot1.decisionQuality ?? null)
  const dqData2 = comparisons.map(c => c.pilot2.decisionQuality ?? null)

  if (loadingInit) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="animate-spin inline-block mb-3">
          <RefreshCw className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-gray-500 text-sm">Loading competition data…</p>
      </div>
    )
  }

  if (error && tasks.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm font-medium mb-1">Failed to load Albatross data</p>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
        <ArrowLeftRight className="h-5 w-5 text-fwo-blue" />
        <h3 className="text-lg font-semibold text-gray-900">Head-to-Head Comparison</h3>
      </div>

      <div className="p-6">
        {/* Pilot Pickers */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
          <div className="flex-1 w-full">
            <label htmlFor="pilot1-select" className="block text-sm font-medium text-gray-700 mb-1">
              Pilot A
            </label>
            <select
              id="pilot1-select"
              value={pilot1Id}
              onChange={e => setPilot1Id(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-fwo-blue focus:outline-none focus:ring-1 focus:ring-fwo-blue"
            >
              <option value="">Select a pilot…</option>
              {participants.map(p => (
                <option key={p.id} value={p.id} disabled={p.id === pilot2Id}>
                  {p.name}
                  {p.participantNumber ? ` (#${p.participantNumber})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end pb-1">
            <Users className="h-5 w-5 text-gray-400" />
          </div>

          <div className="flex-1 w-full">
            <label htmlFor="pilot2-select" className="block text-sm font-medium text-gray-700 mb-1">
              Pilot B
            </label>
            <select
              id="pilot2-select"
              value={pilot2Id}
              onChange={e => setPilot2Id(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-fwo-blue focus:outline-none focus:ring-1 focus:ring-fwo-blue"
            >
              <option value="">Select a pilot…</option>
              {participants.map(p => (
                <option key={p.id} value={p.id} disabled={p.id === pilot1Id}>
                  {p.name}
                  {p.participantNumber ? ` (#${p.participantNumber})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end w-full sm:w-auto">
            <button
              onClick={runComparison}
              disabled={!pilot1Id || !pilot2Id || pilot1Id === pilot2Id || loadingComparison}
              className="w-full sm:w-auto px-6 py-2 bg-fwo-blue text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loadingComparison ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Comparing…
                </>
              ) : (
                'Compare'
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-amber-800 text-sm">{error}</p>
          </div>
        )}

        {/* No tasks in competition yet */}
        {tasks.length === 0 && (
          <div className="text-center py-8">
            <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No tasks available yet.</p>
          </div>
        )}

        {/* Prompt to select pilots */}
        {tasks.length > 0 && comparisons.length === 0 && !loadingComparison && (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <ArrowLeftRight className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              Select two pilots and click <strong>Compare</strong> to see their head-to-head stats across all tasks.
            </p>
          </div>
        )}

        {/* Results */}
        {comparisons.length > 0 && stats1 && stats2 && (
          <>
            {/* Overall aggregated stats */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Overall Averages ({comparisons.length} task{comparisons.length !== 1 ? 's' : ''})
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard
                  label="Climb Rate"
                  val1={stats1.climbRate}
                  val2={stats2.climbRate}
                  name1={pilot1Name}
                  name2={pilot2Name}
                  decimals={2}
                  unit=" m/s"
                />
                <StatCard
                  label="Glide Ratio"
                  val1={stats1.glideRatio}
                  val2={stats2.glideRatio}
                  name1={pilot1Name}
                  name2={pilot2Name}
                  decimals={1}
                />
                <StatCard
                  label="XC Speed"
                  val1={stats1.speed}
                  val2={stats2.speed}
                  name1={pilot1Name}
                  name2={pilot2Name}
                  decimals={1}
                  unit=" km/h"
                />
                <StatCard
                  label="Decision Quality"
                  val1={stats1.decisionQuality}
                  val2={stats2.decisionQuality}
                  name1={pilot1Name}
                  name2={pilot2Name}
                  decimals={0}
                  unit="/100"
                />
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between text-sm">
                  <span className="text-gray-500">Goals reached</span>
                  <span className="font-medium text-gray-800">
                    {pilot1Name}: {stats1.goalsReached} &nbsp;·&nbsp; {pilot2Name}: {stats2.goalsReached}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between text-sm">
                  <span className="text-gray-500">Tasks completed</span>
                  <span className="font-medium text-gray-800">
                    {pilot1Name}: {stats1.tasksCompleted} &nbsp;·&nbsp; {pilot2Name}: {stats2.tasksCompleted}
                  </span>
                </div>
              </div>
            </div>

            {/* Sparkline trends */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Per-Task Trends
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Climb Rate', d1: climbData1, d2: climbData2 },
                  { label: 'Glide Ratio', d1: glideData1, d2: glideData2 },
                  { label: 'XC Speed', d1: speedData1, d2: speedData2 },
                  { label: 'Decision', d1: dqData1, d2: dqData2 },
                ].map(({ label, d1, d2 }) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500 mb-2">{label}</p>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-4 shrink-0">A</span>
                        <Sparkline data={d1} color="#3B82F6" width={80} height={24} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-4 shrink-0">B</span>
                        <Sparkline data={d2} color="#F59E0B" width={80} height={24} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-task table */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Task Breakdown
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 font-medium text-gray-600 whitespace-nowrap">Task</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-600" colSpan={2}>Climb (m/s)</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-600" colSpan={2}>Glide</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-600" colSpan={2}>Speed</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-600" colSpan={2}>Decision</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-600" colSpan={2}>Rank</th>
                    </tr>
                    <tr className="border-b border-gray-100 text-xs text-gray-400">
                      <th />
                      <th className="py-1 px-1 text-center font-normal text-fwo-blue">A</th>
                      <th className="py-1 px-1 text-center font-normal text-amber-500">B</th>
                      <th className="py-1 px-1 text-center font-normal text-fwo-blue">A</th>
                      <th className="py-1 px-1 text-center font-normal text-amber-500">B</th>
                      <th className="py-1 px-1 text-center font-normal text-fwo-blue">A</th>
                      <th className="py-1 px-1 text-center font-normal text-amber-500">B</th>
                      <th className="py-1 px-1 text-center font-normal text-fwo-blue">A</th>
                      <th className="py-1 px-1 text-center font-normal text-amber-500">B</th>
                      <th className="py-1 px-1 text-center font-normal text-fwo-blue">A</th>
                      <th className="py-1 px-1 text-center font-normal text-amber-500">B</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisons.map((c, idx) => (
                      <tr key={c.taskId} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="py-2 pr-4 font-medium text-gray-800 whitespace-nowrap">
                          {c.taskName ?? `Task ${idx + 1}`}
                          {c.date && (
                            <span className="ml-1 text-xs text-gray-400">
                              {new Date(c.date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-1 text-center text-fwo-blue">{fmt(c.pilot1.climbRate, 2)}</td>
                        <td className="py-2 px-1 text-center text-amber-600">{fmt(c.pilot2.climbRate, 2)}</td>
                        <td className="py-2 px-1 text-center text-fwo-blue">{fmt(c.pilot1.glideRatio, 1)}</td>
                        <td className="py-2 px-1 text-center text-amber-600">{fmt(c.pilot2.glideRatio, 1)}</td>
                        <td className="py-2 px-1 text-center text-fwo-blue">{fmt(c.pilot1.speed, 1)}</td>
                        <td className="py-2 px-1 text-center text-amber-600">{fmt(c.pilot2.speed, 1)}</td>
                        <td className="py-2 px-1 text-center text-fwo-blue">{fmt(c.pilot1.decisionQuality, 0)}</td>
                        <td className="py-2 px-1 text-center text-amber-600">{fmt(c.pilot2.decisionQuality, 0)}</td>
                        <td className="py-2 px-1 text-center text-fwo-blue">
                          {c.pilot1.rank != null ? `#${c.pilot1.rank}` : '—'}
                        </td>
                        <td className="py-2 px-1 text-center text-amber-600">
                          {c.pilot2.rank != null ? `#${c.pilot2.rank}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
